import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for handling card swipe interactions
 * Implements Tinder-like swipe functionality with infinite loop and keyboard support
 * Added flippable card functionality for cards with is_flippable=true
 * Refactored swipe mechanism for improved smoothness and reliability.
 */
export default class extends Controller {
  static targets = ["swipeContainer", "subscriptionPrompt", "previewModePrompt"]
  static values = { 
    cards: Array,
    favorites: Array,
    chapterColor: String,
    initialCardId: String,
    previewMode: Boolean
  }

  connect() {   
    if (!this.hasCardsValue) {
      console.warn('No cards value provided')
    }

    // Store original cards data for infinite loop
    this.originalCards = [...this.cardsValue]
    
    this.initializeKeyboardControls()
    this.initializeCards()
    this.initializeHeartButton()

    // Debug logging (retained from original)
    console.log('Has initial card id?', this.hasInitialCardIdValue)
    console.log('Initial card id value:', this.initialCardIdValue)
    console.log('Raw data attribute:', this.element.dataset.swipeInitialCardIdValue)

    if (this.hasInitialCardIdValue) {
      console.log('Moving to card:', this.initialCardIdValue)
      this.moveToCard(this.initialCardIdValue)
    }

    if (this.previewModeValue) {
      this.initializePreviewMode();
    }
  }

  /**
   * Set up keyboard controls for swiping
   */
  initializeKeyboardControls() {
    // Bind the event listener method to `this` context
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);
    
    this.swipeContainerTarget.setAttribute('aria-label', 
      'Card stack. Use K key to like a card, F key to favorite it. ' +
      'Cards can also be swiped right to like or use the heart button to favorite.'
    );
  }

  // Separate method for keydown handling to properly remove listener later if needed (e.g. in disconnect)
  handleKeyDown(event) {
    const currentCard = this.getCurrentCard();
    if (!currentCard) return;

    switch (event.key.toLowerCase()) {
      case 'k':
        // Simulate a right swipe when 'K' is pressed
        // Use a positive X value to indicate right swipe for dismissCard
        this.dismissCard(currentCard, this.swipeContainerTarget.offsetWidth, 0);
        this.handleCardAction(currentCard, 'like');
        break;
      case 'f':
        // Trigger heart button click to get animations
        const heartButton = document.getElementById('heartButton');
        if (heartButton) {
          heartButton.click();
        }
        break;
    }
  }

  // Example: Clean up event listeners on disconnect
  disconnect() {
    document.removeEventListener('keydown', this.handleKeyDown);
    // Any other cleanup for listeners attached to document/window
  }

  getCurrentCard() {
    const cards = Array.from(this.swipeContainerTarget.querySelectorAll('.card:not(.dismissing)')); // Exclude cards being dismissed
    return cards.sort((a, b) => {
      const aZ = parseInt(a.style.zIndex) || 0;
      const bZ = parseInt(b.style.zIndex) || 0;
      return bZ - aZ;
    })[0];
  }

  initializeCards() {
    if (!this.cardsValue || !Array.isArray(this.cardsValue)) {
      console.warn('Cards value is not properly initialized');
      return;
    }

    if (!this.hasSwipeContainerTarget) {
      console.error('No swipe container target found');
      return;
    }

    this.swipeContainerTarget.innerHTML = '';

    this.cardsValue.forEach((cardData, index) => {
      const card = this.createCard(cardData, index);
      this.swipeContainerTarget.appendChild(card);
      this.initializeDragListeners(card);
      if (cardData.is_flippable) {
        this.initializeFlipListeners(card);
      }
    });
  }

  createCard(cardData, index) {
    const card = document.createElement('div');
    card.className = 'card';
    card.style.zIndex = this.cardsValue.length - index;
    card.dataset.cardId = cardData.id;
    
    if (cardData.is_flippable) {
      card.classList.add('flippable');
      card.dataset.flipped = 'false';
    }

    const innerContainer = document.createElement('div');
    innerContainer.className = 'card-inner';
    
    const frontSide = document.createElement('div');
    frontSide.className = 'card-side card-front';
    frontSide.style.background = `linear-gradient(140deg, 
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.8) 100%
    ), ${this.chapterColorValue || '#ffffff'}`;
    
    frontSide.innerHTML = `
      <div class="card-content ${cardData.template_type}-template">
        ${this.renderCardContent(cardData)}
      </div>
      ${cardData.is_flippable ? `<img src="${window.assetPaths.images.flipButton}" class="flip-button" alt="Flip card">` : ''}
    `;

    if (cardData.is_flippable) {
      const backSide = document.createElement('div');
      backSide.className = 'card-side card-back';
      backSide.style.background = frontSide.style.background; // Match front side background
      backSide.innerHTML = `
        <div class="card-content">
          <p>${cardData.back_content.text_content}</p>
        </div>
        <img src="${window.assetPaths.images.flipButton}" class="flip-button" alt="Flip card">
      `;
      innerContainer.appendChild(backSide);
    }

    innerContainer.appendChild(frontSide);
    card.appendChild(innerContainer);
    return card;
  }

  initializeFlipListeners(card) {
    const flipButtons = card.querySelectorAll('.flip-button');
    flipButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation(); 
        this.flipCard(card);
      });
    });
  }

  flipCard(card) {
    if (card.classList.contains('dragging')) {
      return;
    }
    const isFlipped = card.dataset.flipped === 'true';
    card.dataset.flipped = (!isFlipped).toString();
    card.querySelector('.card-inner').style.transform = isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)';
  }

  renderCardContent(cardData) {
    const templates = {
      basic: (data) => `
          ${data.front_content.title ? `
            <h3 style="margin-bottom: 10px; text-align: center; font-weight: bold;">
              ${data.front_content.title}
            </h3>
          ` : ''}
          <p style="font-size: 16px; text-align: center;">
            ${data.front_content.text}
          </p>
          ${data.front_content.note ? `
            <p style="font-size: 14px; font-style: italic; margin-top: 10px; text-align: center;">
              ${data.front_content.note}
            </p>
          ` : ''}
      `,
      image: (data) => {
        const normalizedTitle = data.front_content.title
          .toLowerCase()
          .replace(/[\u2018\u2019\u201C\u201D'',\-\.`"]/g, '')
          .replace(/\s+/g, '_')
          .trim();
        return `
          <h3 style="margin-bottom: 10px; text-align: left; font-weight: bold;">
            ${data.front_content.title}
          </h3>
          <div style="flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
            <img 
              src="${window.assetPaths.images[normalizedTitle]}"
              alt="${data.front_content.title}"
              style="height: auto; max-height: 200px; width:100%; max-width: 100%;"
            />
          </div>
          <p style="font-size: 14px; text-align: left;">
            ${data.front_content.text}
          </p>
        `;
      },
      featured: (data) => `
        <div class="featured-banner bg-yellow-500 text-white px-2 py-1 rounded-tr-lg rounded-bl-lg 
                    absolute top-0 right-0">
          Featured
        </div>
        <div class="card-image mb-4">
          <img src="${data.image_url}" alt="${data.image_alt || ''}" 
               class="w-full h-64 object-cover rounded-lg">
        </div>
        <h3 class="text-xl font-bold mb-2">${data.title}</h3>
        <p class="mb-4">${data.content}</p>
        ${data.cta_button ? `
          <button class="bg-blue-500 text-white px-4 py-2 rounded-lg w-full">
            ${data.cta_button}
          </button>
        ` : ''}
      `
    };

    const templateFn = templates[cardData.template_type] || templates.basic;
    if (!this.validateCardData(cardData)) {
      console.error(`Invalid card data for template type: ${cardData.template_type}, using basic template.`);
      // Ensure cardData.front_content exists for basic template fallback
      cardData.front_content = cardData.front_content || { text: "Error: Card content missing." };
      return templates.basic(cardData);
    }
    return templateFn(cardData);
  }

  validateCardData(cardData) {
    const requiredFields = {
      basic: ['text'],
      image: ['title', 'text'],
      featured: ['title', 'content', 'image_url'] // Assuming 'featured' template needs these in front_content
    };
    
    const templateType = cardData.template_type || 'basic';
    // For 'featured', data might be at root or in front_content. Original code implies root.
    const sourceObject = (templateType === 'featured') ? cardData : cardData.front_content;

    if (!sourceObject) return false;

    const fields = requiredFields[templateType] || requiredFields.basic;
    return fields.every(field => sourceObject[field] != null);
  }

  
  // --- Corrected Improved Swipe Logic ---
  initializeDragListeners(card) {
    let isDragging = false;
    let startPointerX = 0;
    let capturedStartPointerY = 0; // To track Y movement from the start of the gesture
    let cardInitialX = 0;
    let cardInitialY = 0;
    let dragStartTime = 0;

    const pointerDownHandler = (e) => {
      if (e.target.classList.contains('flip-button')) return;
      if (e.type === 'mousedown' && e.button !== 0) return; // Only primary mouse button

      isDragging = true;
      dragStartTime = Date.now();
      card.classList.add('dragging');
      card.style.transition = 'none'; // Disable transitions for direct manipulation

      startPointerX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      capturedStartPointerY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      
      const computedStyle = window.getComputedStyle(card);
      const transform = computedStyle.getPropertyValue('transform');
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        cardInitialX = matrix.m41;
        cardInitialY = matrix.m42;
      } else {
        cardInitialX = 0;
        cardInitialY = 0;
      }

      document.addEventListener('mousemove', pointerMoveHandler);
      document.addEventListener('mouseup', pointerUpHandler);
      document.addEventListener('touchmove', pointerMoveHandler, { passive: false });
      document.addEventListener('touchend', pointerUpHandler);
      document.addEventListener('touchcancel', pointerUpHandler);
      
      // Prevent text selection during drag for mouse
      if (e.type === 'mousedown') {
        e.preventDefault();
      }
    };

    const pointerMoveHandler = (e) => {
      if (!isDragging) return;
      if (e.type === 'touchmove') {
        e.preventDefault(); // Prevent scroll during card drag on touch devices
      }

      const currentPointerX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const currentPointerY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      
      const deltaX = currentPointerX - startPointerX;
      const deltaY = currentPointerY - capturedStartPointerY; // Calculate Y delta from gesture start

      const newTranslateX = cardInitialX + deltaX;
      const newTranslateY = cardInitialY + deltaY; // Apply Y translation
      const rotate = deltaX * 0.1;

      card.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px) rotate(${rotate}deg)`;

      const opacityThreshold = this.swipeContainerTarget.offsetWidth * 0.4;
      const opacity = Math.max(1 - Math.abs(deltaX) / opacityThreshold, 0);
      card.style.opacity = opacity;

      this.updateSwipeIndicators(deltaX);
    };

    const pointerUpHandler = (e) => {
      if (!isDragging) return;
      isDragging = false;
      card.classList.remove('dragging');
      // Transitions will be re-applied by dismissCard or resetCard

      document.removeEventListener('mousemove', pointerMoveHandler);
      document.removeEventListener('mouseup', pointerUpHandler);
      document.removeEventListener('touchmove', pointerMoveHandler);
      document.removeEventListener('touchend', pointerUpHandler);
      document.removeEventListener('touchcancel', pointerUpHandler);

      const finalTransformStyle = window.getComputedStyle(card).getPropertyValue('transform');
      let finalCardX = 0, finalCardY = 0; // These are the absolute positions after drag
      if (finalTransformStyle && finalTransformStyle !== 'none') {
        const matrix = new DOMMatrix(finalTransformStyle);
        finalCardX = matrix.m41;
        finalCardY = matrix.m42;
      } else { // Should not happen if drag occurred, but as a fallback
        finalCardX = cardInitialX; 
        finalCardY = cardInitialY;
      }

      // Displacement is relative to where the card *started* this specific drag gesture
      const displacementX = finalCardX - cardInitialX; 

      const SWIPE_THRESHOLD = this.swipeContainerTarget.offsetWidth * 0.4;
      const timeDelta = Date.now() - dragStartTime;
      const velocity = (timeDelta > 0) ? Math.abs(displacementX / timeDelta) : 0;
      const VELOCITY_THRESHOLD = 0.75; // pixels per millisecond

      if (Math.abs(displacementX) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
        // Use finalCardX (absolute position) to determine swipe direction for dismiss animation
        // and finalCardY for the vertical position of the dismiss animation.
        this.dismissCard(card, finalCardX, finalCardY, displacementX < 0 ? 'left' : 'right');
        this.handleCardAction(card, displacementX < 0 ? 'dislike' : 'like');
      } else {
        this.resetCard(card);
      }
    };

    card.addEventListener('mousedown', pointerDownHandler);
    card.addEventListener('touchstart', pointerDownHandler, { passive: false }); // passive:false for touchstart to allow preventDefault in pointerDown if needed
  }

  dismissCard(card, currentX, currentY, direction = null) { // Added direction for clarity
    card.classList.add('dismissing');
    card.style.transition = 'transform 0.5s, opacity 0.5s';
    
    // Determine swipe direction if not explicitly provided
    // 'currentX' is the card's final absolute X position before animation.
    // If currentX is already far off screen (e.g. keyboard swipe), use that.
    // Otherwise, use the explicit 'direction' or infer from currentX relative to center.
    let swipeDirection = direction;
    if (!swipeDirection) {
        // A simple check if currentX is significantly off-center, otherwise assume based on sign
        // This mainly caters to keyboard dismissal where currentX is already an extreme value.
        swipeDirection = currentX < (this.swipeContainerTarget.offsetWidth / -2) ? 'left' : 
                         currentX > (this.swipeContainerTarget.offsetWidth / 2) ? 'right' :
                         (currentX < 0 ? 'left' : 'right');
    }

    const endX = swipeDirection === 'left' ? -1000 : 1000;
    const rotation = swipeDirection === 'left' ? -60 : 60;
    
    card.style.transform = `translate(${endX}px, ${currentY}px) rotate(${rotation}deg)`;
    card.style.opacity = '0';
    
    setTimeout(() => {
      card.remove();
      
      // Original logic for replenishing or showing prompt
      if (this.previewModeValue) {
        // In preview mode, the overridden replenishCards handles the prompt
        if(this.isReplenishOverridden && this.swipeContainerTarget.querySelectorAll('.card:not(.dismissing)').length === 0) {
            this.replenishCards();
        }
      } else if (this.swipeContainerTarget.children.length < 2) {
        this.replenishCards();
      }

      const heartButton = document.getElementById('heartButton');
      if (heartButton) {
        this.updateHeartButtonState(heartButton);
      }
    }, 500);
  }

  resetCard(card) {
    card.style.transition = 'transform 0.5s, opacity 0.5s';
    // Reset to its original position before drag (0,0 relative to its stacking context)
    card.style.transform = 'translate(0px, 0px) rotate(0deg)'; 
    card.style.opacity = '1';
    
    setTimeout(() => {
      if (card && !card.classList.contains('dragging')) {
         card.style.transition = ''; // Clear transition for next drag
      }
    }, 500);
  }


  replenishCards() {
    // This method might be overridden by initializePreviewMode
    const newCards = [...this.originalCards];
    
    newCards.forEach((cardData, index) => {
      const card = this.createCard(cardData, this.cardsValue.length - 1 - index); // Adjust zIndex for prepending
      this.swipeContainerTarget.insertBefore(card, this.swipeContainerTarget.firstChild);
      this.initializeDragListeners(card);
      if (cardData.is_flippable) {
        this.initializeFlipListeners(card);
      }
    });

    // Re-calculate z-index for all cards
    Array.from(this.swipeContainerTarget.children).forEach((childCard, i) => {
      childCard.style.zIndex = this.swipeContainerTarget.children.length - 1 - i;
    });

    const heartButton = document.getElementById('heartButton');
    if (heartButton) {
      this.updateHeartButtonState(heartButton);
    }
  }

  handleCardAction(card, action) {
    const cardId = card.dataset.cardId;
    // console.log(`Card ${cardId} action: ${action}`);
    if (action === 'like') {
      // Logic for "liking" a card (e.g., API call)
      // This is called after dismissal for right swipes or 'K' key.
      return;
    }
    // Potentially handle 'dislike' action here too
  }

  addToFavorites(card) {
    const cardId = card.dataset.cardId;
    const csrf = document.querySelector("meta[name='csrf-token']").getAttribute("content");
    
    const requestOptions = {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        'X-CSRF-Token': csrf 
      },
      body: JSON.stringify({ card_data: { card_id: cardId } })
    };

    fetch('/add_to_list', requestOptions)
      .then(response => response.json())
      .then(data => {
        this.favoritesValue = data; // Assuming data is the updated list of favorite IDs
        // Update UI accordingly, e.g., heart button state for the new current card if it changed
        const heartButton = document.getElementById('heartButton');
        if (heartButton) this.updateHeartButtonState(heartButton);

        // Original comment: "Removed animation of the card away as per instructions"
        // Original code called: this.handleCardAction(card, 'like'); which does nothing visible.
        // This means favoriting does NOT dismiss the card by itself. This behavior is maintained.
      })
      .catch(error => {
        console.error('Error adding to favorites:', error);
      });
  }

  initializeHeartButton() {
    const heartButton = document.getElementById('heartButton');
    if (heartButton) {
      this.updateHeartButtonState(heartButton); // Initial state

      heartButton.addEventListener('click', () => {
        heartButton.innerHTML = `<img src="${window.assetPaths.images.heartFilled}" alt="Heart" width="88" height="88">`;
        const rect = heartButton.getBoundingClientRect();
        const flutterHearts = [];
        
        for (let i = 0; i < 3; i++) {
          const heart = document.createElement('div');
          heart.innerHTML = `<svg width="88" height="112" viewBox="0 0 88 112" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M46.4116 50.876C44.5124 50.8485 42.5321 51.399 40.9974 52.9446C39.4508 51.402 37.4515 50.8759 35.6038 50.8759C31.0125 50.8759 26.416 54.6798 26.416 60.0633C26.416 65.254 29.736 69.5753 33.0196 72.5132C34.6811 73.9997 36.3851 75.1798 37.769 75.9925C38.4605 76.3986 39.0838 76.7199 39.5923 76.9436C39.8457 77.0551 40.0835 77.1481 40.2953 77.2153C40.4796 77.2737 40.7393 77.3445 40.9998 77.3445C41.2603 77.3445 41.52 77.2737 41.7042 77.2153C41.916 77.1481 42.1538 77.0551 42.4072 76.9436C42.9158 76.7199 43.5391 76.3986 44.2306 75.9925C45.6145 75.1798 47.3185 73.9997 48.9799 72.5132C52.2636 69.5753 55.5835 65.254 55.5835 60.0633C55.5835 54.6671 50.9764 50.942 46.4116 50.876Z" fill="#F67D6C"/>
          </svg>`;
          heart.className = `flutter-heart flutter-heart-${i}`;
          heart.style.position = 'fixed';
          heart.style.left = `${rect.left + rect.width/2 - 44}px`;
          heart.style.top = `${rect.top + rect.height/2 - 66}px`;
          heart.style.zIndex = '1000';
          document.body.appendChild(heart);
          flutterHearts.push(heart);
        }

        flutterHearts.forEach(heart => {
          heart.addEventListener('animationend', () => heart.remove());
        });
      
        const currentCard = this.getCurrentCard();
        if (currentCard) {
          this.addToFavorites(currentCard);
        }
      });
    }
  }

  updateHeartButtonState(heartButton) {
    const currentCard = this.getCurrentCard();
    if (currentCard && this.favoritesValue) {
      const cardId = currentCard.dataset.cardId;
      const isFavorited = this.favoritesValue.includes(parseInt(cardId));
      
      heartButton.classList.toggle('favorited', isFavorited);
      heartButton.innerHTML = `<img src="${isFavorited ? 
        window.assetPaths.images.heartFilled : 
        window.assetPaths.images.heartOutline}" 
        alt="Heart" width="88" height="88">`;
    } else if (heartButton) { // Ensure button is reset if no current card
        heartButton.classList.remove('favorited');
        heartButton.innerHTML = `<img src="${window.assetPaths.images.heartOutline}" 
        alt="Heart" width="88" height="88">`;
    }
  }

  updateSwipeIndicators(deltaX) {
    // TODO: Implement visual indicators for swipe direction (e.g., show like/dislike icons)
    // This was a TODO in original, kept as is.
  }

  moveToCard(cardId) {
    console.log('Moving to card:', cardId);
    // Ensure cardsValue is the current set of cards, not originalCards
    const currentCardElements = Array.from(this.swipeContainerTarget.children);
    let cardIndexInDOM = -1;

    for(let i = 0; i < currentCardElements.length; i++) {
        if(currentCardElements[i].dataset.cardId === cardId) {
            cardIndexInDOM = i;
            break;
        }
    }
    
    console.log('Found card at DOM index:', cardIndexInDOM);
    
    if (cardIndexInDOM === -1) {
      console.debug(`Card ${cardId} not found in current DOM stack.`);
      // It might also be that the card ID is in this.cardsValue but not yet rendered if many cards
      // Original logic seemed to rely on cardsValue being the source of truth for order
      const cardDataIndex = this.cardsValue.findIndex(card => card.id.toString() === cardId);
      if (cardDataIndex !== -1 && cardDataIndex >= currentCardElements.length) {
          console.debug(`Card ${cardId} is further in the data stack, not removing prior DOM cards.`);
          // This case is complex: if initialCardId is beyond currently rendered cards.
          // For now, if not in DOM, we assume it's an issue or it's already passed.
          return;
      } else if (cardDataIndex === -1) {
          console.debug(`Card ${cardId} not found in cardsValue data either.`);
          return;
      }
      // If found in cardsValue but not DOM at expected position, it might be complex.
      // Sticking to original logic of removing based on cardsValue index from DOM.
      // The original logic was:
      // const cardIndex = this.cardsValue.findIndex(card => card.id.toString() === cardId)
      // cardsToRemove = Array.from(this.swipeContainerTarget.children).slice(0, cardIndex)
      // This implies this.cardsValue maps directly to the order in DOM children.
      // Let's assume this.cardsValue is the source of truth for *initial* full deck order.
    }

    // The original logic implies removing cards that appear *before* the target card *in the initial data*.
    // This means we need to map card IDs in DOM to their original indices.
    const targetCardDataIndex = this.originalCards.findIndex(c => c.id.toString() === cardId);
    if (targetCardDataIndex === -1) {
        console.warn(`Card ID ${cardId} for moveToCard not found in originalCards.`);
        return;
    }

    const cardsToRemove = [];
    currentCardElements.forEach(domCard => {
        const domCardId = domCard.dataset.cardId;
        const domCardDataIndex = this.originalCards.findIndex(c => c.id.toString() === domCardId);
        if (domCardDataIndex !== -1 && domCardDataIndex < targetCardDataIndex) {
            cardsToRemove.push(domCard);
        }
    });
    
    console.log('Cards to remove to reach target:', cardsToRemove.map(c => c.dataset.cardId));
    cardsToRemove.forEach(card => card.remove());

    // After removing, update heart button for the new top card
    const heartButton = document.getElementById('heartButton');
    if (heartButton) {
        this.updateHeartButtonState(heartButton);
    }
    // Also potentially re-initialize z-indexes if order is critical after removal
     Array.from(this.swipeContainerTarget.children).forEach((childCard, i) => {
      childCard.style.zIndex = this.swipeContainerTarget.children.length - 1 - i;
    });
  }

  initializePreviewMode() {
    this.isReplenishOverridden = true; // Flag to indicate override
    // Show subscription prompt after last preview card
    const showSubscriptionPrompt = () => {
      // Check if there are truly no cards left in the swipe container that are not being dismissed
      const activeCards = this.swipeContainerTarget.querySelectorAll('.card:not(.dismissing)').length;
      if (activeCards === 0) {
        if (this.hasSubscriptionPromptTarget) this.subscriptionPromptTarget.classList.remove('hidden');
        this.swipeContainerTarget.classList.add('hidden');
        if (this.hasPreviewModePromptTarget) {
          this.previewModePromptTarget.classList.add('hidden');
        }
      }
    };

    this.replenishCards = () => {
      showSubscriptionPrompt();
    };
  }
}